import {
    Badge,
    Button,
    Card,
    Divider,
    Group,
    Stack,
    Text,
    Title,
} from '@mantine/core'
import { ShippingOption } from '@ortros/stoux-lib'
import {
    Check,
} from 'lucide-react'
import clsx from 'clsx'
import { useMemo } from 'react'

import { StoreOption } from '@/domain/Cart/dtos/list-shipping-options-by-cart.dto'
import formatAmount from '@/utils/format-amount'
import { pluralWord } from '@/utils/plural-word'
import { getStore } from '@/utils/store'

type Props = {
    dataSource: StoreOption
    selectedShippingOptionId: string
    onSelectShippingOption: (shippingOption: ShippingOption) => void
}
const StoreShippingOption = ({
    dataSource,
    selectedShippingOptionId,
    onSelectShippingOption,
}: Props) => {
    const totalAmount = useMemo(() => {
        return dataSource.line_items.reduce(
            (acc, item) => acc + item.unit_price,
            0,
        )
    }, [dataSource.line_items])

    const store = getStore()
    return (
        <Card withBorder>
            <Stack>
                <Stack spacing={2}>
                    <Text
                        component="span"
                        color="gray.6"
                        size="xs"
                        weight="normal"
                    >
                        <b>{dataSource.line_items.length}</b>{' '}
                        {pluralWord('article', dataSource.line_items.length)}{' '}
                        pour un total de <b>{formatAmount(totalAmount)}</b>
                    </Text>
                    {!store && (
                        <Text className="text-sm text-neutral-600">
                            Vendu par <b>{dataSource.storeName}</b>
                        </Text>
                    )}
                </Stack>
            </Stack>

            <Divider my="sm" />

            <Stack className="justify-start">
                <Text className="text-sm font-semibold text-neutral-600">
                    Options de livraison
                </Text>
                <div>
                    {!!dataSource.shipping_options.length &&
                        [...dataSource.shipping_options]
                        .sort((a, b) => {
                            const amountA = a.amount ?? (a.data.flat_amount as number)
                            const amountB = b.amount ?? (b.data.flat_amount as number)
                            return amountA - amountB
                        })
                        .map((so) => {
                            const isSelected =
                                selectedShippingOptionId === so.id

                            const amountOfItems = dataSource.line_items.length

                            const amount = so.amount
                                ? so.amount
                                : (so.data.flat_amount as number)

                            return (
                                <Card
                                    className={clsx(
                                        'flex items-center justify-between p-4 transition-all duration-200 rounded cursor-pointer hover:bg-gray-200',
                                        isSelected && 'bg-blue-50',
                                    )}
                                    key={so.id}
                                    w="100%"
                                >
                                    <Group>
                                        <Stack spacing={0}>
                                            <Text
                                                className={clsx(
                                                    'text-sm tracking-tight font-bold',
                                                    isSelected &&
                                                        'text-blue-400',
                                                )}
                                            >
                                                {so.name}
                                            </Text>
                                            {!!so.data
                                                .additional_amount_per_item && (
                                                <Text
                                                    component="span"
                                                    color="gray.6"
                                                    size="xs"
                                                    weight="normal"
                                                >
                                                    +{' '}
                                                    {formatAmount(
                                                        so.data
                                                            .additional_amount_per_item as number,
                                                    )}{' '}
                                                    / article supplémentaire
                                                </Text>
                                            )}
                                            {!!so.metadata?.informations && (
                                                <Text
                                                    component="span"
                                                    color="gray.6"
                                                    size="xs"
                                                    weight="normal"
                                                >
                                                    {so.metadata.informations as string}
                                                </Text>
                                            )}
                                        </Stack>
                                        <Stack className="w-full">
                                            <>
                                                <Badge
                                                    className="transition-all duration-200 max-w-max"
                                                    variant={
                                                        isSelected
                                                            ? 'filled'
                                                            : 'outline'
                                                    }
                                                    color={
                                                        isSelected
                                                            ? 'blue'
                                                            : 'dark.3'
                                                    }
                                                >
                                                    {so.includes_tax && 'TTC'}
                                                    {formatAmount(amount)} DE
                                                    FRAIS
                                                </Badge>

                                                {isSelected && (
                                                    <Stack className="w-full">
                                                        <Divider my={3} />
                                                        <Text
                                                            component="span"
                                                            color="gray.6"
                                                            size="xs"
                                                            weight="normal"
                                                        >
                                                            Soit un total de{' '}
                                                            <b>
                                                                {formatAmount(
                                                                    totalAmount +
                                                                        amount +
                                                                        (amountOfItems >
                                                                        1
                                                                            ? (so
                                                                                  .data
                                                                                  .additional_amount_per_item as number) *
                                                                              (amountOfItems -
                                                                                  1)
                                                                            : 0),
                                                                )}
                                                            </b>
                                                        </Text>
                                                    </Stack>
                                                )}
                                            </>
                                        </Stack>
                                    </Group>
                                    <Button
                                        variant={
                                            isSelected ? 'subtle' : 'outline'
                                        }
                                        color={isSelected ? 'blue' : 'dark.3'}
                                        size="xs"
                                        onClick={() =>
                                            onSelectShippingOption(so)
                                        }
                                    >
                                        {isSelected ? (
                                            <Check className="w-4 h-4" />
                                        ) : (
                                            'Sélectionner'
                                        )}
                                    </Button>
                                </Card>
                            )
                        })}
                    {!dataSource.shipping_options.length && (
                        <Text
                            component="span"
                            color="gray.6"
                            size="xs"
                            weight="normal"
                        >
                            Aucune méthode de livraison disponible pour ce
                            magasin.
                        </Text>
                    )}
                </div>
            </Stack>
        </Card>
    )
}

export default StoreShippingOption
